import { useContext, useMemo } from "react";

import { DataContext } from "contexts/Data";
import useVariant from "./useVariant";

import type { Variant } from "types/app";

const allowedTypes = ["string", "number"];

/**
 * @see https://stackoverflow.com/a/66144780/11625355
 */
type PickKeysMatching<T, V> = keyof { [P in keyof T as T[P] extends V ? P : never]: P };

type Available = {
  selected: boolean;
  code: string;
  /**
   * All variants sharing the distinct code
   */
  group: Variant[];
};

/**
 * Only use this hook under DataContext
 * @param reducerKey - Keys with string or number type of value (at `Variant[reducerKey]`), others are simply ignored
 */
export default function useDistinctVariantTypes(
  reducerKey: PickKeysMatching<Variant, string | number | undefined>,
  customVariants?: Variant[],
  sortFn?: (a: string, b: string) => number,
): Available[] {
  const { variants } = useContext(DataContext);
  const variant = useVariant();

  return useMemo(() => {
    if (!variants || !variant) return [];

    const models = (customVariants || variants).reduce(
      (models, variant) => {
        if (reducerKey in variant && allowedTypes.includes(typeof variant[reducerKey])) {
          const key = variant[reducerKey] as string | number;
          if (key in models) models[key].push(variant);
          else models[key] = [variant];

          return models;
        }

        return models;
      },
      {} as Record<string, Variant[]>,
    );

    return Object.keys(models)
      .sort(sortFn)
      .map(
        (modelCode) =>
          ({
            code: modelCode,
            selected: modelCode === variant[reducerKey]?.toString(),
            group: models[modelCode],
          }) as Available,
      );
  }, [variants, variant, reducerKey, customVariants]); // eslint-disable-line react-hooks/exhaustive-deps
}
